import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Layers, Trash2, Settings2, GitBranch } from "lucide-react";
import camApi from "@/lib/camApi";
import { useCam } from "@/components/cam/ctx";
import { PickerSelect } from "@/components/cam/AssetDrawer";

const TYPES = ["text", "number", "date", "boolean", "select"];

export default function Categories() {
  const { categories, reloadLookups } = useCam();
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);

  const refresh = () => reloadLookups();

  const addCategory = async () => {
    if (!name.trim()) return;
    try { await camApi.createCategory({ name: name.trim() }); setName(""); toast.success("Category added"); await refresh(); }
    catch (e) { toast.error(e.message); }
  };
  const removeCategory = async (id) => {
    if (!window.confirm("Deactivate this category?")) return;
    try { await camApi.removeCategory(id); toast.success("Removed"); await refresh(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="lg:col-span-2 glass rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1"><Layers className="h-4 w-4 text-cobalt-500" /><div className="overline">Asset categories</div></div>
        <p className="text-xs text-muted-foreground mb-3">Group assets (Desktops, Furniture, Oscilloscopes…). Each category can define its own specification fields — no schema change needed.</p>
        <div className="flex gap-2 max-w-md">
          <Input placeholder="New category name…" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()} />
          <Button className="bg-cobalt-500 hover:bg-cobalt-600" onClick={addCategory}><Plus className="h-4 w-4 mr-1" />Add</Button>
        </div>
      </div>

      {categories.map((c) => (
        <div key={c.id} className="glass rounded-2xl p-4">
          <div className="flex items-center gap-2">
            <div className="font-display text-base tracking-tight flex-1">{c.name}</div>
            <button onClick={() => setEditing(editing === c.id ? null : c.id)} className="text-muted-foreground hover:text-cobalt-500 p-1" title="Fields & sub-categories"><Settings2 className="h-4 w-4" /></button>
            <button onClick={() => removeCategory(c.id)} className="text-muted-foreground hover:text-red-500 p-1"><Trash2 className="h-4 w-4" /></button>
          </div>

          {c.subcategories?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {c.subcategories.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1 text-[11px] rounded-full bg-cobalt-500/10 text-cobalt-600 px-2 py-0.5">
                  <GitBranch className="h-3 w-3" />{s.name}
                </span>
              ))}
            </div>
          )}

          <div className="mt-2 flex flex-wrap gap-1.5">
            {c.fields?.length ? c.fields.map((f) => {
              const sub = c.subcategories?.find((s) => s.id === f.subcategory_id);
              return (
                <span key={f.id || f.key} className="inline-flex items-center gap-1 text-[11px] rounded-md bg-black/5 dark:bg-white/10 px-2 py-0.5">
                  {f.label}<span className="text-muted-foreground">·{f.data_type}</span>
                  {sub && <span className="text-cobalt-600">· {sub.name}</span>}
                </span>
              );
            }) : <span className="text-xs text-muted-foreground">No custom fields</span>}
          </div>
          {editing === c.id && <FieldEditor category={c} onDone={refresh} />}
        </div>
      ))}
      {!categories.length && <div className="text-sm text-muted-foreground px-1">No categories yet.</div>}
    </div>
  );
}

function FieldEditor({ category, onDone }) {
  const blank = { key: "", label: "", data_type: "text", is_required: false, options: "", scope: "cat" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [subName, setSubName] = useState("");
  const subs = category.subcategories || [];

  const addSub = async () => {
    if (!subName.trim()) return;
    try { await camApi.addSubcategory(category.id, { name: subName.trim() }); setSubName(""); toast.success("Sub-category added"); await onDone(); }
    catch (e) { toast.error(e.message); }
  };
  const removeSub = async (id) => {
    if (!window.confirm("Remove this sub-category? Its fields go with it.")) return;
    try { await camApi.removeSubcategory(id); toast.success("Removed"); await onDone(); } catch (e) { toast.error(e.message); }
  };

  const add = async () => {
    if (!f.label.trim()) { toast.error("Label is required."); return; }
    setBusy(true);
    try {
      const key = (f.key || f.label).toLowerCase().replace(/[^a-z0-9_]/g, "_");
      const options = f.data_type === "select" ? f.options.split(",").map((s) => s.trim()).filter(Boolean) : [];
      const subcategory_id = f.scope !== "cat" ? Number(f.scope) : null;
      await camApi.addField(category.id, { key, label: f.label.trim(), data_type: f.data_type, is_required: f.is_required, options, subcategory_id });
      toast.success("Field added"); setF(blank); await onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const remove = async (id) => { try { await camApi.removeField(id); toast.success("Field removed"); await onDone(); } catch (e) { toast.error(e.message); } };

  const scopeName = (fld) => subs.find((s) => s.id === fld.subcategory_id)?.name;

  return (
    <div className="mt-3 rounded-xl border p-3 space-y-4 bg-white/40">
      {/* Sub-categories */}
      <div>
        <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-muted-foreground"><GitBranch className="h-3.5 w-3.5" />Sub-categories</div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {subs.length ? subs.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 text-xs rounded-full bg-cobalt-500/10 text-cobalt-600 pl-2.5 pr-1 py-1">
              {s.name}
              <button onClick={() => removeSub(s.id)} className="hover:text-red-500 rounded-full p-0.5"><Trash2 className="h-3 w-3" /></button>
            </span>
          )) : <span className="text-xs text-muted-foreground">None yet — e.g. under Computers: Desktop, Laptop, All-in-One.</span>}
        </div>
        <div className="flex gap-1.5 max-w-xs">
          <Input className="h-8 text-sm" placeholder="Add sub-category…" value={subName} onChange={(e) => setSubName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSub()} />
          <Button size="sm" variant="outline" className="h-8" onClick={addSub}><Plus className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {/* Fields */}
      <div className="border-t pt-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Specification fields</div>
        {category.fields?.map((fld) => (
          <div key={fld.id} className="flex items-center gap-2 text-sm py-0.5">
            <span className="flex-1">
              {fld.label} <span className="text-muted-foreground text-xs">({fld.data_type}{fld.is_required ? ", required" : ""})</span>
              {scopeName(fld) && <span className="text-cobalt-600 text-xs"> · {scopeName(fld)}</span>}
            </span>
            <button onClick={() => remove(fld.id)} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          <div><Label className="text-xs">Field label</Label><Input className="h-9 mt-1" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="e.g. RAM (GB)" /></div>
          <div><Label className="text-xs">Type</Label><PickerSelect placeholder="Type" value={f.data_type} onChange={(v) => setF({ ...f, data_type: v })} options={TYPES.map((t) => ({ value: t, label: t }))} className="mt-1" /></div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Applies to</Label>
            <PickerSelect value={f.scope} onChange={(v) => setF({ ...f, scope: v })}
              options={[{ value: "cat", label: "Whole category" }, ...subs.map((s) => ({ value: String(s.id), label: s.name }))]} className="mt-1" />
          </div>
          {f.data_type === "select" && <div className="sm:col-span-2"><Label className="text-xs">Options (comma-separated)</Label><Input className="h-9 mt-1" value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })} placeholder="Small, Medium, Large" /></div>}
        </div>
        <div className="flex items-center justify-between mt-2">
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_required} onCheckedChange={(v) => setF({ ...f, is_required: v })} />Required</label>
          <Button size="sm" onClick={add} disabled={busy}>{busy ? "…" : "Add field"}</Button>
        </div>
      </div>
    </div>
  );
}
